import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useMfaRequirement } from "@/hooks/useMfaRequirement";
import { ForgePageLoader } from "@/components/ForgeLoader";
import { AccessLoadError } from "./AccessLoadError";
import { createSessionVerificationTimeoutError, useLoadingTimeout } from "@/hooks/useLoadingTimeout";
import {
  logAuthTransition,
  trackRoute,
} from "@/lib/auth-transition-logger";
import {
  getAuthRedirectReason,
  getAuthRedirectTarget,
  hasExplicitSignedOutState,
  useInitialSessionErrorRecovery,
} from "@/components/layout/routeGuardUtils";

interface OrgAdminRouteProps {
  children: React.ReactNode;
}

const ORG_ADMIN_ROUTE_GUARD_LOADING_TIMEOUT_MS = 45_000;

/**
 * Route guard for organization admins only.
 * Requires the user to have an admin role within the current org.
 */
export function OrgAdminRoute({ children }: OrgAdminRouteProps) {
  const {
    isAuthenticated,
    isLoading: authLoading,
    hasSessionError,
    authError,
    lastAuthEvent,
    rehydrateSession,
  } = useAuth();
  const { isOrgAdmin, isLoading: permLoading, rolesUnavailable, retryRoles } = usePermissions();
  const location = useLocation();

  const { status: mfaStatus, needsChallenge, needsSetup, requiresMfa, reauthRequired } = useMfaRequirement();
  const mfaReady = mfaStatus === "checked";
  const isRecoveringInitialSession = useInitialSessionErrorRecovery({
    isAuthenticated,
    isAuthLoading: authLoading,
    hasSessionError,
    lastAuthEvent,
    rehydrateSession,
  });
  const isLoading = permLoading || !mfaReady;
  const isLoadingTimedOut = useLoadingTimeout(
    isAuthenticated && !authLoading && isLoading,
    ORG_ADMIN_ROUTE_GUARD_LOADING_TIMEOUT_MS,
  );

  const currentRoute = `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;

  // Track route changes for auth transition logging
  useEffect(() => {
    trackRoute(currentRoute);
  }, [currentRoute]);

  // STEP 1: Wait for auth check to complete.
  if (authLoading || isRecoveringInitialSession) {
    return <ForgePageLoader />;
  }

  const hasConfirmedSignedOutState = hasExplicitSignedOutState(
    isAuthenticated,
    lastAuthEvent,
  );

  // STEP 2 (auth resolved): Throw session errors to boundary.
  if (hasSessionError && !isAuthenticated && !hasConfirmedSignedOutState) {
    const errorMessage = authError?.message || "Session check failed";
    const error = new Error(`Session verification failed: ${errorMessage}`);
    error.name = "SessionVerificationError";
    throw error;
  }

  // STEP 3 (auth resolved): Redirect unauthenticated users immediately.
  if (!isAuthenticated) {
    const redirectReason = getAuthRedirectReason(lastAuthEvent);

    logAuthTransition(redirectReason, {
      lastAuthEvent,
      fromRoute: currentRoute,
      routeGuard: "OrgAdminRoute",
    });

    const authTarget = getAuthRedirectTarget(location);
    return <Navigate to={authTarget} state={{ from: location }} replace />;
  }

  // STEP 4: User is authenticated — now wait for permissions and MFA.
  if (isLoading && isLoadingTimedOut) {
    throw createSessionVerificationTimeoutError(
      "Session verification is taking longer than expected while loading organization administrator permissions and MFA status.",
    );
  }

  if (isLoading) {
    return <ForgePageLoader />;
  }

  if (rolesUnavailable) {
    return <AccessLoadError onRetry={() => { retryRoles?.(); }} />;
  }

  if (!isOrgAdmin) {
    return <Navigate to="/403" replace />;
  }

  if (requiresMfa && mfaReady) {
    if (needsSetup && location.pathname !== "/mfa/setup") {
      return <Navigate to="/mfa/setup" state={{ from: location }} replace />;
    }

    if (needsChallenge && location.pathname !== "/mfa/challenge") {
      const challengePath = reauthRequired ? "/mfa/challenge?reauth=1" : "/mfa/challenge";
      return <Navigate to={challengePath} state={{ from: location }} replace />;
    }
  }

  return <>{children}</>;
}
